import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { colors } from '../styles/colors';
import ActionButton from './ActionButton';
import ModalPortal from './ModalPortal';

/**
 * SessionExpiredModal - Modale de reconnexion après expiration de session.
 *
 * Affiche un formulaire de connexion pré-rempli avec l'email de l'utilisateur.
 * Si la reconnexion réussit, la modale se ferme et l'utilisateur reste sur la page
 * courante (travail non perdu). Un lien "Changer de compte" permet de forcer
 * la déconnexion et de revenir à la page login.
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen        - État d'ouverture
 * @param {string}   props.userEmail     - Email pré-rempli (lecture seule)
 * @param {function} props.onReconnect   - (password) => Promise<{success, error?}>
 * @param {function} props.onLogout      - Déconnexion forcée + redirect /login
 */
function SessionExpiredModal({ isOpen, userEmail, onReconnect, onLogout }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const passwordRef = useRef(null);

  // Reset et focus à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setError('');
      setLoading(false);
      setShowPassword(false);
      // Légère attente pour que le DOM soit monté
      setTimeout(() => passwordRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!password.trim() || loading) return;

    setLoading(true);
    setError('');

    const result = await onReconnect(password);

    if (!result.success) {
      setError(result.error || 'Identifiants incorrects. Veuillez réessayer.');
      setLoading(false);
      passwordRef.current?.focus();
    }
    // Si success : AuthContext ferme la modale → pas besoin de gérer ici
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        // Pas de fermeture au clic extérieur (l'utilisateur doit agir)
      >
        <div
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '1rem',
            width: '90%',
            maxWidth: '420px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: colors.primary.gold,
              color: colors.text.white,
              padding: '1rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}
          >
            <AlertTriangle size={24} />
            <h3
              style={{
                margin: 0,
                fontSize: '1.125rem',
                fontWeight: '700',
                fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
              }}
            >
              Session expirée
            </h3>
          </div>

          {/* Contenu */}
          <div style={{ padding: '1.5rem' }}>
            <p
              style={{
                margin: '0 0 1.25rem 0',
                fontSize: '0.9375rem',
                color: colors.primary.gray,
                lineHeight: '1.5'
              }}
            >
              Votre session a expiré. Saisissez votre mot de passe pour continuer.
            </p>

            <form onSubmit={handleSubmit}>
              {/* Email (lecture seule) */}
              <div style={{ marginBottom: '0.875rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: colors.primary.gray
                  }}
                >
                  Email
                </label>
                <input
                  type="email"
                  value={userEmail}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    borderRadius: '0.5rem',
                    border: `1px solid ${colors.border.light}`,
                    backgroundColor: colors.background.lightGray,
                    color: colors.text.secondary,
                    fontSize: '0.9375rem',
                    boxSizing: 'border-box',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              {/* Mot de passe */}
              <div style={{ marginBottom: error ? '0.75rem' : '1.25rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: colors.primary.gray
                  }}
                >
                  Mot de passe
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={passwordRef}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '0.625rem 2.5rem 0.625rem 0.875rem',
                      borderRadius: '0.5rem',
                      border: `1px solid ${error ? colors.status.error : colors.border.light}`,
                      backgroundColor: colors.background.input,
                      color: colors.primary.gray,
                      fontSize: '0.9375rem',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: colors.text.secondary,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Message d'erreur */}
              {error && (
                <p
                  style={{
                    margin: '0 0 1rem 0',
                    fontSize: '0.875rem',
                    color: colors.status.error
                  }}
                >
                  {error}
                </p>
              )}

              {/* Bouton principal */}
              <ActionButton
                text={loading ? 'Connexion...' : 'Se reconnecter'}
                onClick={handleSubmit}
                variant="secondary"
                disabled={loading || !password.trim()}
                style={{ width: '100%' }}
              />
            </form>

            {/* Lien de déconnexion forcée */}
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={onLogout}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  color: colors.text.secondary,
                  fontSize: '0.875rem',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                Changer de compte
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default SessionExpiredModal;
